import { useRef, useState } from "react";
import { AlertTriangle, FileUp, Upload } from "lucide-react";
import {
  useExportDocumentMutation,
  useImportDocumentMutation,
} from "@/api/api";
import { HGImportDocument, HGImportNode, HGImportResult } from "@/common/types";
import { globalReloader } from "@/providers/dbRecordsLoader";
import { cn } from "@/lib/utils";
import { Button } from "../button";
import { Checkbox } from "../checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../dialog";
import { Field, FieldLabel } from "../field";

// Refused before upload rather than after: a file this size is never a
// legitimate document, and the server caps the body at 5 MB anyway.
const MAX_BYTES = 5 * 1024 * 1024;

function countNodes(nodes: HGImportNode[] | undefined): number {
  if (!nodes) return 0;
  return nodes.reduce(
    (total, node) => total + 1 + countNodes(node.children),
    0,
  );
}

function countMedia(nodes: HGImportNode[] | undefined): number {
  if (!nodes) return 0;
  return nodes.reduce(
    (total, node) =>
      total + (node.media?.length ?? 0) + countMedia(node.children),
    0,
  );
}

/** What the file says it is, or why it cannot be one. */
function readDocument(text: string): {
  document?: HGImportDocument;
  error?: string;
} {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return { error: `That file is not valid JSON (${(e as Error).message}).` };
  }
  const document = parsed as HGImportDocument;
  if (
    !document ||
    typeof document !== "object" ||
    !Array.isArray(document.nodes)
  )
    return { error: "That file does not look like an Eventera import." };
  if (document.format !== "eventera.import")
    return {
      error: `That file says its format is "${document.format ?? "missing"}", not "eventera.import".`,
    };
  if (!document.source)
    return {
      error: "That file has no source, so nothing could be matched up.",
    };
  return { document };
}

const ACTION_STYLES: Record<string, string> = {
  create: "text-emerald-700 dark:text-emerald-400",
  update: "text-sky-700 dark:text-sky-400",
  unchanged: "text-muted-foreground",
  skipped: "text-amber-700 dark:text-amber-500",
  failed: "text-destructive",
};

function Summary({ result }: { result: HGImportResult }) {
  const c = result.counts;
  const parts = [
    c.collections_created && `${c.collections_created} collections added`,
    c.collections_updated && `${c.collections_updated} collections updated`,
    c.records_created && `${c.records_created} records added`,
    c.records_updated && `${c.records_updated} records updated`,
    c.unchanged && `${c.unchanged} unchanged`,
    c.skipped && `${c.skipped} skipped`,
    c.failed && `${c.failed} failed`,
    c.pruned && `${c.pruned} deleted`,
    c.media_added && `${c.media_added} images attached`,
    c.media_failed && `${c.media_failed} images failed`,
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13px]">
        {result.dry_run ? "This file would make: " : "Done: "}
        <span className="font-medium">
          {parts.length ? parts.join(", ") : "no changes"}
        </span>
        .
      </p>

      {result.problems.length > 0 && (
        <ul className="flex flex-col gap-1 rounded-[10px] border border-amber-500/50 bg-amber-500/5 px-3 py-2 text-[12px]">
          {result.problems.map((problem, i) => (
            <li key={i}>{problem}</li>
          ))}
        </ul>
      )}

      {result.orphans.length > 0 && (
        <div className="rounded-[10px] border border-border bg-secondary px-3 py-2 text-[12px]">
          <p className="font-medium">
            {result.orphans.length} thing(s) this file no longer describes
          </p>
          <ul className="mt-1 flex flex-col gap-0.5 text-muted-foreground">
            {result.orphans.map((orphan) => (
              <li key={`${orphan.entity_type}-${orphan.entity_id}`}>
                {orphan.entity_type}: {orphan.title || "Untitled"}
                {orphan.cascade_records > 0 &&
                  ` — deleting it would also delete ${orphan.cascade_records} record(s)`}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="max-h-56 overflow-y-auto rounded-[10px] border border-border">
        <table className="w-full text-[12px]">
          <tbody>
            {result.nodes.map((node) => (
              <tr
                key={node.id}
                className="border-b border-border last:border-b-0"
              >
                <td className="px-3 py-1.5">{node.title || node.id}</td>
                <td className="px-2 py-1.5 text-muted-foreground">
                  {node.kind}
                </td>
                <td
                  className={cn(
                    "px-3 py-1.5 text-right",
                    ACTION_STYLES[node.action],
                  )}
                >
                  {node.action}
                  {node.message && (
                    <span className="block text-[11px] text-muted-foreground">
                      {node.message}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ImportDialog({
  collectionID,
  collectionTitle,
}: {
  // Where the tree is attached. Undefined imports at the top of the space.
  collectionID?: string;
  collectionTitle?: string;
}) {
  const [open, setOpen] = useState(false);
  const [document_, setDocument] = useState<HGImportDocument | null>(null);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<HGImportResult | null>(null);
  const [prune, setPrune] = useState(false);
  const [importMedia, setImportMedia] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [importDocument, { isLoading }] = useImportDocumentMutation();

  const reset = () => {
    setDocument(null);
    setFileName("");
    setError(null);
    setResult(null);
    setPrune(false);
    setImportMedia(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    setOpen(next);
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setResult(null);
    setError(null);
    if (file.size > MAX_BYTES) {
      setError("That file is larger than 5 MB.");
      setDocument(null);
      return;
    }
    const { document: parsed, error: problem } = readDocument(
      await file.text(),
    );
    if (problem) {
      setError(problem);
      setDocument(null);
      return;
    }
    setFileName(file.name);
    setDocument(parsed ?? null);
    // A file that lists images almost certainly means them, so the option
    // arrives ticked — but only when there is something to tick it for, and it
    // is still the user who presses Import.
    setImportMedia(countMedia(parsed?.nodes) > 0);
  };

  const run = async (dryRun: boolean) => {
    if (!document_) return;
    setError(null);
    try {
      const response = await importDocument({
        document: document_,
        target_collection_id: collectionID ?? null,
        dry_run: dryRun,
        prune,
        import_media: importMedia,
      }).unwrap();
      setResult(response);
      // The timeline's records live outside both Redux and the RTK Query cache,
      // so the tags this mutation invalidates never reach them.
      if (!dryRun) globalReloader.reload();
    } catch (e) {
      const detail = (e as { data?: { detail?: string | string[] } })?.data
        ?.detail;
      setError(
        Array.isArray(detail)
          ? detail.join("\n")
          : detail || "The import could not be run.",
      );
    }
  };

  const nodeCount = countNodes(document_?.nodes);
  const mediaCount = countMedia(document_?.nodes);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full">
          <Upload />
          Import
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            Import{" "}
            {collectionTitle ? `into “${collectionTitle}”` : "collections"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <input
            ref={inputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <Button
            variant="secondary"
            onClick={() => inputRef.current?.click()}
            className="justify-start"
          >
            <FileUp />
            {fileName || "Choose a JSON file…"}
          </Button>

          {document_ && (
            <p className="text-[12px] text-muted-foreground">
              {nodeCount} node(s), source{" "}
              <span className="font-mono">{document_.source}</span>. Anything
              this source imported before is updated in place rather than added
              again.
            </p>
          )}

          {error && (
            <p className="whitespace-pre-line rounded-[10px] border border-destructive bg-destructive/5 px-3 py-2 text-[12px] text-destructive">
              {error}
            </p>
          )}

          {document_ && mediaCount > 0 && (
            <Field orientation="horizontal">
              <Checkbox
                id="importMedia"
                checked={importMedia}
                onCheckedChange={(checked) => setImportMedia(checked === true)}
              />
              <FieldLabel htmlFor="importMedia" className="font-normal">
                Attach the {mediaCount} image(s) this file lists
                <span className="block text-[11px] text-muted-foreground">
                  Each one is downloaded from its URL, so a large file takes a
                  while. Images already attached are never fetched twice, so
                  importing again picks up anything that did not make it.
                </span>
              </FieldLabel>
            </Field>
          )}

          {document_ && (
            <Field orientation="horizontal">
              <Checkbox
                id="importPrune"
                checked={prune}
                onCheckedChange={(checked) => setPrune(checked === true)}
              />
              <FieldLabel htmlFor="importPrune" className="font-normal">
                <span className="flex items-center gap-1.5">
                  {prune && (
                    <AlertTriangle size={13} className="text-destructive" />
                  )}
                  Delete what this file no longer describes
                </span>
                {prune && (
                  // Not a normal delete: delete_collection cascades through
                  // record_collection, so a record filed in a pruned collection
                  // *and* somewhere else is destroyed too.
                  <span className="block text-[11px] text-destructive">
                    Deleting a collection also deletes its records, including
                    ones filed in other collections. Preview first.
                  </span>
                )}
              </FieldLabel>
            </Field>
          )}

          {result && <Summary result={result} />}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" type="button">
              {result && !result.dry_run ? "Close" : "Cancel"}
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="secondary"
            disabled={!document_ || isLoading}
            onClick={() => run(true)}
          >
            Preview
          </Button>
          <Button
            type="button"
            disabled={!document_ || isLoading}
            onClick={() => run(false)}
          >
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Download the current scope as a document that imports back onto itself. */
export function ExportButton({
  collectionID,
  collectionTitle,
}: {
  collectionID?: string;
  collectionTitle?: string;
}) {
  const [exportDocument, { isLoading }] = useExportDocumentMutation();
  const [problems, setProblems] = useState<string[]>([]);

  const handleExport = async () => {
    setProblems([]);
    try {
      const result = await exportDocument({
        collection_id: collectionID ?? null,
        // Stable per scope, so exporting the same collection twice and
        // importing either file lands on the same records.
        source: collectionID ? `export:${collectionID}` : "export",
      }).unwrap();

      const blob = new Blob([JSON.stringify(result.document, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const anchor = window.document.createElement("a");
      anchor.href = url;
      anchor.download = `${collectionTitle || "eventera"}.json`.replace(
        /[/\\?%*:|"<>]/g,
        "-",
      );
      anchor.click();
      URL.revokeObjectURL(url);
      setProblems(result.problems);
    } catch {
      setProblems(["The export could not be built."]);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        disabled={isLoading}
        onClick={handleExport}
      >
        <Upload className="rotate-180" />
        Export
      </Button>
      {problems.length > 0 && (
        <ul className="flex flex-col gap-1 text-[11px] text-muted-foreground">
          {problems.map((problem, i) => (
            <li key={i}>{problem}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
